import * as z from 'zod/v4';

import { assertType_EnumAndArrayMatch } from '@infinex-private/ts-utils/assert';
import type { DenominationCategory } from '@infinex/accounting';
import type {
  AssetMetadataV2,
  ChainAssetBalancePositionData,
  LstAssetMetadata,
  NftMetadata,
  PointType,
} from '@infinex/asset-config';
import type { ChainKey } from '@infinex/chain-config';
import type {
  AggregatedCratePrize,
  CrateOpening,
  CratePrize,
  CratePrizeRarity,
  CratePrizeType,
  RafflePrize,
  RaffleWin,
} from '@infinex/crate-config';

import type { BridgeXyzDataset } from './datasets/bridge-xyz';
import type { ChainsDataset } from './datasets/chains';
import type { InxDataset } from './datasets/inx';
import type { MultiSigDetailsDataset } from './datasets/multi-sig-details';
import type { WalletsV2Dataset } from './datasets/wallets-v2';

import type { AirdropCampaignAllocations } from '../airdrop';
import type { AssetRate } from '../asset-price';
import type {
  CollectAmount,
  CollectibleCompany,
  CollectibleGachaBuyback,
  CollectibleStatus,
  CollectibleType,
  CollectorCryptGachaRarity,
  CollectorCryptShippingRedemptionStatus,
  CollectMarketplaceOpenOffersAggregate,
  FoilCategory,
} from '../collectibles';
import { COLLECTOR_CRYPT_GACHA_RARITIES } from '../collectibles';
import type {
  FeatureFlag,
  PlatformFeatureFlag,
  ResolvedFeatureFlag,
} from '../feature-flag';
import type { PasskeyStatus } from '../passkey';
import type {
  AccountSnapshot,
  PerpsBalance,
  ProviderStatus,
} from '../perps/domain/contracts';
import type {
  MarketIdentity,
  MarketProductKind,
} from '../perps/domain/providerRegistry';
import type { Event as TransactionalEvent } from '../websocket';

export * from './datasets/inx';
export * from './datasets/bridge-xyz';
export * from './datasets/chains';
export * from './datasets/multi-sig-details';
export * from './datasets/wallets-v2';

export const DATASET_KEYS = [
  'bridgeXyz',
  'chains',
  'ledgerBalances',
  'multiSigs',
  'cratePrizes',
  'crateRafflePrizes',
  'featureFlags',
  'inx',
  'user',
  'turnkey',
  'vaults',
  'walletsV2',
  'airdropCampaignAllocations',
  'chainBalances',
  'collectibles',
  'perps',
] as const;

export const datasetKeyEnum = z.enum(DATASET_KEYS);

export interface Datasets {
  airdropCampaignAllocations: AirdropCampaignAllocationsDataset;
  bridgeXyz: BridgeXyzDataset;
  chains: ChainsDataset;
  featureFlags: FeatureFlagsDataset;
  inx: InxDataset;
  ledgerBalances: LedgerBalancesDataset;
  multiSigs: MultiSigDetailsDataset;
  user: UserDataset;
  turnkey: TurnkeyDataset;
  vaults: VaultsDataset;
  walletsV2: WalletsV2Dataset;
  cratePrizes: CratePrizesDataset;
  crateRafflePrizes: CrateRafflePrizesDataset;
  chainBalances: ChainBalancesDataset;
  collectibles: CollectiblesDataset;
  perps: PerpsDataset;
}

export type DatasetKey = keyof Datasets;

export const datasetKeys: ReadonlyArray<DatasetKey> =
  assertType_EnumAndArrayMatch<DatasetKey>()(DATASET_KEYS);

export type UserDataset = {
  id: string;
  username: string;
  details: {
    email: string | null;
    createdAt: Date;
    lastLoginAt: Date | null;
    referralCode: string;
  };
  status: {
    emailVerified: boolean;
    totpEnrolled: boolean;
    isTurnkeyWalletRecoveryEnabled: boolean;
    isConnectEnabled: boolean;
    isDisabled: boolean | null;
    isPatron: boolean;
  };
  recovery: {
    evmFundsRecoveryAddress: string | null;
    solanaFundsRecoveryAddress: string | null;
    turnkeyRecoveryEmail: string | null;
    turnkeyRecoveryUserId: string | null;
  };
  addresses: {
    evmOperationKeyAddress: `0x${string}` | null;
    evmAccountAddress: `0x${string}` | null;
    evmWalletAddress: `0x${string}` | null;
    solanaDepositAddress: string | null;
    solanaWalletAddress: string | null;
    originKeyAddress: `0x${string}` | null;
  };
  turnkey: {
    ownerUserId: string | null;
    operationUserId: string | null;
    subOrgId: string | null;
    /** Turnkey wallet ID of the main (non-imported) Infinex wallet. */
    operationWalletId: string | null;
  };
  uiState: Record<string, unknown>;
  passkeys: Array<{
    passkeyId: string;
    publicKey: string;
    counter: number;
    status: PasskeyStatus | null;
  }>;
  _entitlements: {
    // Data related to computing entitlements.
    // TODO @riva extract the entitlements logic in a single place
    balancePatronNfts: number;
  };
};

export type FeatureFlagsDataset = {
  /**
   * @deprecated - read from `featureFlags.<feature flag>` instead
   *
   * TODO: remove this once the extension reaches production (ask @sneedinfinex before removing)
   **/
  [key in FeatureFlag]?: boolean;
} & {
  featureFlags: Partial<Record<PlatformFeatureFlag, ResolvedFeatureFlag>>;
};

export type VaultsDatasetVault = {
  id: string; // UUID from vault.id in database
  chain: string;
  address: string;
  fundsRecoveryAddress: string | null;
  isDeployed: boolean;
};

export interface VaultsDataset {
  vaults: VaultsDatasetVault[];
  hasAnyVaultsConfigured: boolean;
  canConfigureVaults: boolean;
  upgradeAvailableByChain: Record<ChainKey, boolean>;
}

export type PointBalance = {
  balance: string;
  lastUpdated: Date;
  pointsType: PointType;
  chain: ChainKey | null;
  hide: boolean;
};

export type InxBalances = {
  liquid: string;
  locked: string;
  total: string;
};

export type CollectibleValueSource =
  | 'insured_value'
  | 'listing_price'
  | 'oracle_price';

export interface CollectibleImages {
  front: string | null;
  frontM: string | null;
  frontS: string | null;
  back: string | null;
  backM: string | null;
  backS: string | null;
  /**
   * The DAS/arweave image URL, carried alongside the Collector Crypt CDN slots
   * so clients can fall back away from CC's CDN. Optional because upstream CC
   * marketplace payloads ship an `images` object without it, and the additive
   * listing merge validates upstream values against this shape.
   */
  das?: string | null;
}

export function emptyCollectibleImages(): CollectibleImages {
  return {
    front: null,
    frontM: null,
    frontS: null,
    back: null,
    backM: null,
    backS: null,
    das: null,
  };
}

/**
 * The pokemontcg.io card a Collector Crypt collectible resolved to (1:1 on the
 * canonical (setId, number) key) plus our Cloudinary-mirrored art. Attached to a
 * {@link Collectible} as `pokemontcg` when matched; absent (null) otherwise.
 */
export interface CollectiblePokemontcg {
  /** Stable pokemontcg.io id, e.g. "swsh9-18". */
  id: string;
  /** Canonical pokemontcg.io set id, e.g. "swsh9", "swshp" — drives the foil
   * shader's promo-specific selectors (keyed on setId + number). Always present:
   * the card is matched 1:1 on (setId, number). */
  setId: string;
  /** pokemontcg.io set series, e.g. "Sword & Shield" — resolves the card's
   * layout era (which drives the holo foil clip region). */
  series: string | null;
  /** pokemontcg.io rarity string, e.g. "Rare Holo VMAX". */
  rarity: string | null;
  /** Card number within the set, e.g. "18", "TG02", "111a". Always present (the
   * canonical match key). */
  number: string;
  /** Energy types, e.g. ["Grass"] — drives the foil's energy glow. */
  types: string[] | null;
  /** Subtypes, e.g. ["V", "Single Strike"] — drives stage/full-art clips. */
  subtypes: string[] | null;
  /** Supertype: "Pokémon" | "Trainer" | "Energy". */
  supertype: string | null;
  /** Our Cloudinary-mirrored card art — the foil renders on this. */
  imageUrl: string | null;
}

export interface CollectibleRawAttribute {
  traitType?: string;
  value: string | number | boolean | null;
  displayType?: string;
}

export type Collectible = {
  assetAddress: string;
  companyCollectibleId: string | null;
  collectibleType: CollectibleType | null;
  category: string | null;
  status: CollectibleStatus | null;
  year: number | null;
  name: string;
  description: string | null;
  images: CollectibleImages;
  /** The single "best display image", derived from `images` (CC full-res when
   * available, else the DAS image in `images.das`). The foil-rendered card art
   * lives on `pokemontcg.imageUrl`. */
  imageUrl: string | null;
  insuredValue: string | null;
  oraclePrice: string | null;
  vault: string | null;
  location: string | null;
  vaultId: string | null;
  gradingId: string | null;
  gradingCompany: string | null;
  isAutographed: boolean | null;
  grade: string | null;
  gradeNumber: number | null;
  isAuthenticated: boolean | null;
  setName: string | null;
  serial: string | null;
  subset: string | null;
  parallel: string | null;
  packCount: number | null;
  productCode: string | null;
  gemrateUniversalId: string | null;
  gemrateGrade: string | null;
  specId: string | null;
  setUrl: string | null;
  rawAttributes: CollectibleRawAttribute[];
  /** ISO-8601 string passthrough from Collector Crypt, displayed raw and never
   * parsed — deliberately not a `Date` like `lastUpdatedAt`, which is dataset
   * machinery. */
  lastTransferredAt: string | null;
  // --- Pokémon TCG foil enrichment ---
  // Populated only for Collector Crypt cards that resolved 1:1 to a pokemontcg.io
  // card (see the foil module in @infinex/collectibles); null otherwise. Both
  // fields move together: `pokemontcg` present ⟺ `foilCategory` present.
  /** The matched pokemontcg.io card + its mirrored art. Null = unmatched. */
  pokemontcg: CollectiblePokemontcg | null;
  /** Derived foil shader category (null when unmatched). */
  foilCategory: FoilCategory | null;
};

/**
 * Viewer/market context for a held collectible in the collection dataset.
 * These fields describe the user's holding, active listing, valuation, and
 * buyback affordance; they are not intrinsic collectible identity fields and
 * therefore live alongside `collectible`, not inside it.
 */
export interface CollectibleOwnership {
  walletAddress: string;
  chain: ChainKey;
  company: CollectibleCompany;
  listingPrice: string | null;
  listingMarketplace: string | null;
  value: string | null;
  valueSource: CollectibleValueSource | null;
  isUnpriced: boolean;
  /** ISO-8601 string serialized for client datasets. */
  lastUpdatedAt: string | null;
  /**
   * Gacha buyback affordance for this holding (sell it back to Collector Crypt).
   * Null unless we sold this NFT via gacha and a buyback is currently actionable
   * or in flight. See {@link CollectibleGachaBuyback}.
   */
  buyback: CollectibleGachaBuyback | null;
}

/**
 * @deprecated Root-level collectible fields are retained for stale extension
 * bundles that still read the top-level shape (pre-#15103). Use `collectible`
 * for intrinsic collectible identity/details. Remove once old bundles have aged
 * out (see the #14895 perps precedent for the same pattern).
 */
export type DeprecatedCollectibleHoldingFields = Collectible;

export type CollectibleHolding = DeprecatedCollectibleHoldingFields &
  CollectibleOwnership & {
    collectible: Collectible | null;
  };

/**
 * A single opened gacha pack (one row of `collector_crypt_gacha_purchase` in
 * the `opened` state). One opened pack === one opening, like a single reveal.
 */
export interface GachaOpening {
  id: string;
  requestId: string;
  memo: string;
  packType: string;
  packIndex: number;
  isAutoBuybackOn: boolean;
  wasAutoBoughtBack: boolean;
  rarity: CollectorCryptGachaRarity | null;
  points: number | null;
  roll: string | null;
  nftAddress: string | null;
  openingTxnHash: string | null;
  collectible: Collectible | null;
  /** Payment amount and asset. */
  paymentAmount: CollectAmount;
  /** Auto-buyback refund amount and asset (null if not bought back). */
  buybackAmount: CollectAmount | null;
  openedAt: Date;
}

/**
 * Rolled-up gacha stats + recent openings for the collectibles dataset.
 * For paginated history, use the `getGachaOpenings` endpoint instead.
 */
export interface GachasSummary {
  /** Total number of packs the user has opened */
  totalPacksOpened: number;
  /** Count of opened packs by rarity */
  raritySummary: Record<CollectorCryptGachaRarity, number>;
  /** Sum of payment amounts across opened packs, as a decimal USDC string */
  totalSpentUsdc: string;
  /** Sum of auto-buyback refunds across opened packs, as a decimal USDC string */
  totalBuybackUsdc: string;
  /** The highest-rarity opened pack (null if none) */
  rarestPrize: GachaOpening | null;
  /** Recent openings for reveal flows (bounded) */
  recentOpenings: GachaOpening[];
}

export const EMPTY_GACHAS_SUMMARY: GachasSummary = {
  totalPacksOpened: 0,
  raritySummary: Object.fromEntries(
    COLLECTOR_CRYPT_GACHA_RARITIES.map((rarity) => [rarity, 0])
  ) as Record<CollectorCryptGachaRarity, number>,
  totalSpentUsdc: '0',
  totalBuybackUsdc: '0',
  rarestPrize: null,
  recentOpenings: [],
};

/**
 * A Collector Crypt points balance breakdown — either for a single wallet, or
 * summed across every wallet a user holds. Mirrors the `getPoints` API shape:
 * `totalPoints` is lifetime earned, while `pointsUsed`/`pointsRemaining` track
 * spend (which summing per-open gacha points locally can never capture).
 */
export interface CollectorCryptPointsBreakdown {
  totalPoints: number;
  primaryPoints: number;
  secondaryPoints: number;
  bonusPoints: number;
  pointsUsed: number;
  pointsRemaining: number;
}

/** A points breakdown attributed to one wallet address. */
export interface CollectorCryptPointsByAddress extends CollectorCryptPointsBreakdown {
  address: string;
}

/**
 * The user's Collector Crypt points, broken down per wallet and summed into a
 * single total. `byAddress` only contains wallets whose balance fetch resolved;
 * a transient per-wallet failure simply omits that wallet and self-heals on the
 * next rebuild.
 */
export interface CollectorCryptPointsSummary {
  /** Summed breakdown across every resolved wallet. */
  total: CollectorCryptPointsBreakdown;
  /** Per-wallet breakdown (one entry per resolved SVM address). */
  byAddress: CollectorCryptPointsByAddress[];
}

export const EMPTY_COLLECTOR_CRYPT_POINTS_BREAKDOWN: CollectorCryptPointsBreakdown =
  {
    totalPoints: 0,
    primaryPoints: 0,
    secondaryPoints: 0,
    bonusPoints: 0,
    pointsUsed: 0,
    pointsRemaining: 0,
  };

export const EMPTY_COLLECTOR_CRYPT_POINTS_SUMMARY: CollectorCryptPointsSummary =
  {
    total: { ...EMPTY_COLLECTOR_CRYPT_POINTS_BREAKDOWN },
    byAddress: [],
  };

/**
 * Bounded physical-redemption stats for Collector Crypt shipping. Detailed
 * fulfillment/history stays behind dedicated shipping endpoints; the dataset
 * only carries small derived counts for badges and summary UI.
 */
export interface CollectShippingRedemptionsSummary {
  redemptionCount: number;
  inFlightCount: number;
  confirmedCount: number;
  reviewCount: number;
  errorCount: number;
  latestStatus: CollectorCryptShippingRedemptionStatus | null;
  latestUpdatedAt: Date | null;
}

export const EMPTY_COLLECT_SHIPPING_REDEMPTIONS_SUMMARY: CollectShippingRedemptionsSummary =
  {
    redemptionCount: 0,
    inFlightCount: 0,
    confirmedCount: 0,
    reviewCount: 0,
    errorCount: 0,
    latestStatus: null,
    latestUpdatedAt: null,
  };

export interface CollectiblesDataset {
  generatedAt: number;
  cards: CollectibleHolding[];
  totalValue: string;
  pricedCardCount: number;
  unpricedCardCount: number;
  gachas: GachasSummary;
  /** Collector Crypt points balance, per wallet and totalled. */
  collectorCryptPoints: CollectorCryptPointsSummary;
  openOffers: CollectMarketplaceOpenOffersAggregate[];
  shipping: CollectShippingRedemptionsSummary;
}

// This type only used by the LedgerBalances dataset so I guess it makes sense to define it here
// Ideally we've move towards the more common/correct LedgerBalance type; that's what we use everywhere else
export type UserLedgerBalance = {
  balance: string;
  decimals: number;
  category: DenominationCategory;
  denominationKey: string | null;
  optionStrikePrice: string | null;
  optionExpiresAt: Date | null;
  denominationId: string;
};
export interface LedgerBalancesDataset {
  ledgerBalances: UserLedgerBalance[];
}

export interface CratePrizesDataset {
  /** Aggregated prizes by type (without rarity) */
  aggregated: AggregatedCratePrize[];
  /** Total number of crates opened */
  totalCratesOpened: number;
  /** Breakdown of prizes by rarity */
  raritySummary: Record<CratePrizeRarity, number>;
  /** Breakdown of prizes by type */
  typeSummary: Record<CratePrizeType, number>;
  /** The rarest prize the user has received (null if no prizes) */
  rarestPrize: CratePrize | null;
  /**
   * Recent openings for reveal flow (includes all statuses).
   * For paginated prize history, use getCrateOpenings() endpoint instead.
   */
  recentOpenings: CrateOpening[];
}

export interface CrateRafflePrizesDataset {
  aggregated: RafflePrize[];
  wins: RaffleWin[];
}

export interface AirdropCampaignAllocationsDataset {
  airdropCampaignAllocations: AirdropCampaignAllocations[];
}

export interface PolicyUpgradeInfo {
  policyName: string; // Policy name with version baked in (e.g., OPERATION_SIGNER_POLICY_V2)
  userMessage?: string | null; // For unknown policies, this will contain the notes from Turnkey
}

export interface PolicyComparisonResult {
  adding: PolicyUpgradeInfo[];
  removing: PolicyUpgradeInfo[];
  keeping: PolicyUpgradeInfo[];
}

export interface PoliciesDataset {
  comparisonResult: PolicyComparisonResult;
  updateRequired: boolean;
  availableOptionalPolicies: Array<{
    name: string;
    description: string;
    userMessage?: string | null;
  }>;
}

export interface TurnkeyDataset {
  ownerUserId: string | null;
  operationUserId: string | null;
  subOrgId: string | null;
  recoveryUserId: string | null;
  operationWalletId: string | null;
  policies: PoliciesDataset;
}

export interface DatasetRequestWebSocketMessage {
  type: 'dataset';
  key: DatasetKey;
}

export interface DatasetResponseWebSocketMessage {
  type: 'dataset';
  key: DatasetKey;
  data: unknown;
}

export interface TransactionalEventWebSocketMessage {
  type: 'transactionalEvent';
  event: TransactionalEvent;
}

export type UserWebSocketRequestMessage = DatasetRequestWebSocketMessage;

export type UserWebSocketResponseMessage =
  | DatasetResponseWebSocketMessage
  | TransactionalEventWebSocketMessage;

export type ChainBalanceToken = {
  type: 'token';
  walletAddress: string;
  chain: ChainKey;
  assetAddress: string;
  rawBalance: string;
};

export type ChainBalanceNft = {
  type: 'nft';
  walletAddress: string;
  chain: ChainKey;
  assetAddress: string;
  rawBalance: string;
  isPatron?: boolean;
  nftData?: NftMetadata[];
};

export type ChainBalancePosition = {
  type: 'position';
  walletAddress: string;
  chain: ChainKey;
  assetAddress: string;
  rawBalance: string;
  positionData: ChainAssetBalancePositionData;
};

export type ChainBalance =
  | ChainBalanceToken
  | ChainBalanceNft
  | ChainBalancePosition;

export type ChainBalancesDataset = {
  balances: ChainBalance[];
  metadataV2: AssetMetadataV2[];
  //TODO(dre): Mark this requried after the next release
  lstMetadata: LstAssetMetadata[] | undefined;
  prices: AssetRate[];
  generatedAt: number;
  /** Distinct assets dropped server-side to fit the dataset size budget */
  truncatedAssetCount?: number;
};

export interface MarginSummary {
  accountValue: number;
  totalNtlPos: number;
  totalRawUsd: number;
  totalMarginUsed: number;
}
export type CrossMarginSummary = MarginSummary;

/**
 * A provider's perp clearing-house state. The read-model (`balances` +
 * `accountSnapshots`) is the preferred consumer-facing surface; this legacy
 * array is still emitted on {@link PerpsDataset} for backwards compatibility
 * with older extension versions that consume it directly. The worker builder
 * also uses it internally to derive balances and snapshots.
 */
export type PerpsClearingHouse = {
  walletAddress: string;
  exchange: PerpsExchange;
  dex: string | null;
  marginSummary: MarginSummary;
  crossMarginSummary: CrossMarginSummary;
  withdrawable: number;
  chain: ChainKey;
  assetAddress: string;
};

export type PerpsPosition = {
  walletAddress: string;
  exchange: PerpsExchange;
  dex: string | null;
  coin: string;
  positionValue: number;
  szi: string;
  entryPx: string;
  unrealizedPnl: string;
  returnOnEquity: string;
  leverage: {
    type: string;
    value: number;
  };
  liquidationPx: string | null;
  marginUsed: string;
  maxLeverage: number | null;
  cumFunding: {
    allTime: string;
    sinceOpen: string;
    sinceChange: string;
  } | null;
};

export type PerpsOpenOrder = {
  walletAddress: string;
  exchange: PerpsExchange;
  dex: string | null;
  orderId: number;
  coin: string;
  orderType: string;
  side: string;
  sz: string;
  limitPx: string;
  triggerPx: string;
  reduceOnly: boolean | null;
  oid: number;
  timestamp: number;
  productKind?: MarketProductKind;
  market?: MarketIdentity;
  supportIdentifiers?: Readonly<Record<string, string | number | boolean>>;
};

export type PerpsExchange = 'hyperliquid' | 'synthetix' | 'lighter';

export type PerpsProviderDatasetState = {
  providerId: PerpsExchange;
  status: ProviderStatus;
  freshness: AccountSnapshot['freshness'];
  error?: AccountSnapshot['error'];
};

export type HyperliquidSpotClearingHouseState = {
  address: string;
  balances: {
    coin: string;
    token: number;
    hold: string;
    total: string;
    entryNtl: string;
  }[];
};

export type PerpsDataset = {
  /**
   * @deprecated Prefer the read model (`balances` + `accountSnapshots`). Still
   * emitted for backwards compatibility with older extension versions that read
   * this array directly.
   */
  clearingHouseStates?: PerpsClearingHouse[];
  spotClearingHouseStates?: HyperliquidSpotClearingHouseState[];
  positions: PerpsPosition[];
  openOrders: PerpsOpenOrder[];
  providerStates?: PerpsProviderDatasetState[];
  accountSnapshots?: AccountSnapshot[];
  balances?: PerpsBalance[];
  metadataV2: AssetMetadataV2[];
  prices: AssetRate[];
  generatedAt: number;
};
